import React from 'react';
import {
  ReduxDispatchProps,
  ReduxSelectorProps,
} from '../../../services/constants';
import { AxiosInstance } from 'axios';
import { ComponentConfig } from '@measured/puck';
import { MarginProps, marginFields } from '../../../puck/reusable-props/margin';
import {
  PaddingProps,
  paddingFields,
} from '../../../puck/reusable-props/padding';
import { getcurrentDeviceType } from '../../../utils/helper';
import { Switch, Tooltip } from '@mui/material';
import CustomTooltip from '../../../../components/common/tooltip';
import InfoIcon from '@mui/icons-material/Info';
import Border, {
  borderProps,
  IBorderValueProps,
} from '../../../puck/reusable-props/Border';
import { QuillTextEditor } from '../../../puck/reusable-props/quill-editor';
import '../../text-editor/style.scss';
import { backgroungFields } from '../../../puck/reusable-props/backgroungFields';
import WidthAndHeight, {
  WidthAndHeightSubProps,
} from '../../../puck/reusable-props/widthAndHeight';
import showComponent, {
  showComponentProps,
} from '../../../puck/reusable-props/showComponent';
import ApplePayButtonRender from './applePay';

export interface ApplePayButtonComponentProps
  extends ReduxSelectorProps,
    ReduxDispatchProps {
  baseService?: AxiosInstance;
}

export type ApplePayButtonProps = {
  background: {
    color: string;
    isInherit: boolean;
  };
  buttonCustomization: WidthAndHeightSubProps;
  headerText: string;
  showOnlyButton: boolean;
  isDark: boolean;
  justifyContent: string;
  border: IBorderValueProps;
  TermsAndConCheck: { isTermsAndConChecked: boolean; errorMsg: string };
} & MarginProps &
  PaddingProps &
  showComponentProps;

export const ApplePayButton = ({
  useAppDispatch,
  useAppSelector,
}: ApplePayButtonComponentProps): ComponentConfig<ApplePayButtonProps> => {
  return {
    label: (
      <CustomTooltip title="ApplePayButton">
        <span>Apple Pay</span>
      </CustomTooltip>
    ) as React.ReactNode as string,
    fields: {
      ...showComponent,
      TermsAndConCheck: {
        type: 'custom',
        render: ({ onChange, value }) => {
          return (
            <div className="space-y-4">
              {/* Toggle for isTermsAndConChecked */}
              <div className="flex justify-between items-center">
                <p className="text-sm font-semibold">
                  Require Terms & Conditions
                </p>
                <div className="flex gap-0.5 items-center">
                  <Tooltip
                    title="Toggle to require terms and conditions acceptance"
                    placement="top-start">
                    <InfoIcon fontSize="small" className="h-4 w-4" />
                  </Tooltip>
                  <Switch
                    onChange={e => {
                      onChange({
                        ...value,
                        isTermsAndConChecked: e.target.checked,
                      });
                    }}
                    checked={value?.isTermsAndConChecked}
                    inputProps={{ 'aria-label': 'controlled' }}
                  />
                </div>
              </div>

              {/* Input for errorMsg */}
              {value?.isTermsAndConChecked && (
                <div className="flex flex-col">
                  <label className="text-sm font-semibold mb-1">
                    Error Message for Terms & Conditions
                  </label>
                  <input
                    type="text"
                    value={value?.errorMsg}
                    onChange={e =>
                      onChange({
                        ...value,
                        errorMsg: e.target.value,
                      })
                    }
                    placeholder="Enter error message"
                    className="border border-gray-300 rounded-md p-2 text-sm"
                  />
                </div>
              )}
            </div>
          );
        },
      },
      headerText: {
        ...QuillTextEditor('Header Text').content,
      },
      showOnlyButton: {
        type: 'custom',
        render: ({ onChange, value }) => {
          return (
            <div className="flex justify-between items-center mb-3">
              <p className="text-sm font-semibold">Show only Button</p>
              <div className="flex gap-0.5 items-center">
                <Tooltip
                  title={'Toggle this switch to show only the Button'}
                  placement="top-start">
                  <InfoIcon fontSize="small" className="h-1 w-1" />
                </Tooltip>
                <Switch
                  onChange={e => {
                    onChange(e.target.checked);
                  }}
                  checked={value}
                  inputProps={{ 'aria-label': 'controlled' }}
                />
              </div>
            </div>
          );
        },
      },
      isDark: {
        type: 'custom',
        render: ({ onChange, value }) => {
          return (
            <div className="flex justify-between items-center mb-3">
              <p className="text-sm font-semibold">Show Button as Dark</p>
              <div className="flex gap-0.5 items-center">
                <Tooltip
                  title={'Toggle this switch to show the Button as dark'}
                  placement="top-start">
                  <InfoIcon fontSize="small" className="h-1 w-1" />
                </Tooltip>
                <Switch
                  onChange={e => {
                    onChange(e.target.checked);
                  }}
                  checked={value}
                  inputProps={{ 'aria-label': 'controlled' }}
                />
              </div>
            </div>
          );
        },
      },
      justifyContent: {
        type: 'select',
        label: 'Button Alignment',
        options: [
          { label: 'center', value: 'm-auto' },
          { label: 'left', value: '' },
          { label: 'right', value: 'ml-auto' },
        ],
      },
      buttonCustomization: {
        type: 'custom',
        render: ({ value, onChange }) => (
          <WidthAndHeight
            value={value}
            onChange={val => {
              onChange({
                ...value,
                ...val,
              });
            }}
            title={'Button Customization'}
          />
        ),
      },
      ...backgroungFields,
      ...marginFields,
      ...paddingFields,
      border: {
        type: 'custom',
        render: ({ value, onChange }) => (
          <Border value={value} onChange={onChange} title="Border" />
        ),
      },
    },
    defaultProps: {
      showComponent: ['desktop', 'tablet', 'mobile'],
      buttonCustomization: {
        height: {
          desktop: '48',
          mobile: '48',
          tablet: '48',
        },
        maxWidth: {
          desktop: '160',
          mobile: '160',
          tablet: '160',
        },
        deviceType: 'desktop',
      },
      TermsAndConCheck: {
        isTermsAndConChecked: false,
        errorMsg: '*Please accept the terms and conditions to proceed.',
      },
      background: {
        color: '#ffffff',
        isInherit: false,
      },
      justifyContent: 'm-auto',
      showOnlyButton: false,
      isDark: true,
      headerText:
        '<p style="line-height: 1.5;" class="ql-align-center"><span style="font-size: 16px;">Express checkout</span></p>',
      margin: {
        desktop: {
          top: '8',
          bottom: '4',
          right: '0',
          left: '0',
        },
        tablet: {
          top: '8',
          bottom: '4',
          right: '0',
          left: '0',
        },
        mobile: {
          top: '8',
          bottom: '4',
          right: '0',
          left: '0',
        },
      },
      padding: {
        deviceType: 'desktop',
        paddingBottom: {
          desktop: '4',
          mobile: '4',
          tablet: '4',
        },
        paddingLeft: {
          desktop: '4',
          mobile: '4',
          tablet: '4',
        },
        paddingRight: {
          desktop: '4',
          mobile: '4',
          tablet: '4',
        },
        paddingTop: {
          desktop: '4',
          mobile: '4',
          tablet: '4',
        },
      },
      ...borderProps,
    },
    render: ({
      margin,
      padding,
      isDark,
      editMode,
      showOnlyButton,
      buttonCustomization,
      headerText,
      justifyContent,
      border,
      background,
      TermsAndConCheck,
      showComponent,
    }) => {
      const windowWidth = useAppSelector(state => state.store.currentWidth);
      const currentDeviceType = getcurrentDeviceType(windowWidth, editMode);
      return (
        <>
          {showComponent?.includes(currentDeviceType) && (
            <ApplePayButtonRender
              margin={margin}
              padding={padding}
              isDark={isDark}
              showOnlyButton={showOnlyButton}
              buttonCustomization={buttonCustomization}
              headerText={headerText}
              justifyContent={justifyContent}
              border={border}
              background={background}
              TermsAndConCheck={TermsAndConCheck}
              useAppDispatch={useAppDispatch}
              useAppSelector={useAppSelector}
              editMode={editMode}
            />
          )}
        </>
      );
    },
  };
};
